import { useToast } from '@chakra-ui/react';
import { DrawerType } from '@pelicargo/types';
import { RequestStatus } from '@prisma/client';
import { useHotkeys } from 'react-hotkeys-hook';

import { useRequestDrawersContext } from '../components/RequestDrawers';
import { trpc } from '../config/trpc';
import { GetRequestOutput } from './request/useRequest';
import { useCopyToClipboard } from './useCopyToClipboard';
import { useCustomerUrls } from './useCustomerUrls';

export const useRequestEmails = ({ request }: { request: GetRequestOutput }) => {
  const toast = useToast();
  const { openModal } = useRequestDrawersContext();
  const copy = useCopyToClipboard();

  const { mutateAsync: assertRequestIsValidTo, isLoading: isLoadingBookingAssertion } =
    trpc.assertRequestIsValidTo.useMutation();

  const {
    quoteListingUrl,
    quoteListingPath,
    bookingConfirmationUrl,
    bookingConfirmationPath,
    marketplaceBookingUrl,
    marketplaceBookingPath,
  } = useCustomerUrls({
    requestId: request?.id,
    quoteId: request?.booked_quote_variant?.quote_id,
    quoteVariantId: request?.booked_quote_variant_id,
    tenantId: request?.tenant_id,
    fulfillmentPartnerConfigurationId: request?.booked_quote_variant?.quote?.fulfillment_partner_configuration_id,
  });

  useHotkeys(
    'l>q',
    () => {
      if (!quoteListingUrl) return;
      copy(quoteListingUrl)();
    },
    { preventDefault: true, enabled: !!quoteListingUrl },
  );

  useHotkeys(
    'l>b',
    () => {
      if (!bookingConfirmationUrl) return;
      copy(bookingConfirmationUrl)();
    },
    { preventDefault: true, enabled: !!bookingConfirmationUrl },
  );

  useHotkeys(
    'l>m',
    () => {
      if (!marketplaceBookingUrl) return;
      copy(marketplaceBookingUrl)();
    },
    { preventDefault: true, enabled: !!marketplaceBookingUrl },
  );

  const handleClickSendBooking = async () => {
    try {
      await assertRequestIsValidTo({ id: request.id, desiredStatus: RequestStatus.BookingComplete });
      openModal('BookingConfirmation');
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
    }
  };

  const handleQuickSelectDrawer = (drawerType: DrawerType) => {
    switch (drawerType) {
      case DrawerType.BookingConfirmation:
        return handleClickSendBooking();
      case DrawerType.RequestSchedule: {
        if (!request?.dropoff_by) {
          return toast({
            title: 'Error',
            description: "No 'dropoff by' date is set",
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
        return openModal(drawerType);
      }
      default:
        return openModal(drawerType);
    }
  };

  return {
    isLoadingBookingAssertion,
    handleClickSendBooking,
    handleQuickSelectDrawer,
    copy,
    quoteListingUrl,
    quoteListingPath,
    bookingConfirmationUrl,
    bookingConfirmationPath,
    marketplaceBookingUrl,
    marketplaceBookingPath,
  };
};
